/**
 * ResearchSection - v2 slideout section for research on contacts and companies.
 *
 * Wraps the existing useResearch hook and ResearchDetail/ResearchButton
 * components in a CollapsibleSection matching the v2 slideout style.
 */

import { useQueryClient } from '@tanstack/react-query';
import { ChevronRight, SearchMd } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useProposalSidebar } from '@/contexts/ActionSidebarContext';
import { BadgeWithDot } from '@/lib/base/badges/badges';
import { QueryErrorState } from '@/lib/components';
import { apiPatch } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import { quickEmailProposal } from '@/lib/proposals/core/ActionBuilder';
import { useCreateProposal } from '@/lib/proposals/query/mutations';
import { formatRelativeTime } from '@/lib/utils/timeFormatting';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';
import { cx } from '@/utils/cx';

import { canResearch } from '../../record/research/ResearchButton';
import { ResearchDetail } from '../../record/research/ResearchDetail';
import { ResearchListItem } from '../../record/research/ResearchListItem';
import { ResearchNeedsMoreInfo } from '../../record/research/ResearchNeedsMoreInfo';
import {
  getResearchOutcome,
  getResearchStatusBadge,
} from '../../record/research/researchOutcome';
import { useCreateResearch } from '../../record/research/useCreateResearch';
import type { Research } from '../../record/research/useResearch';
import { useResearch } from '../../record/research/useResearch';
import { CollapsibleSection } from './collapsible-section';
import { SectionActionButton } from './section-action-button';

interface ResearchSectionProps {
  object: CrmObject;
}

function LoadingSkeleton() {
  return (
    <div className='animate-pulse space-y-3'>
      <div className='flex items-center gap-2'>
        <div className='h-5 w-20 bg-[var(--color-bg-tertiary)] rounded' />
        <div className='h-3 w-16 bg-[var(--color-bg-tertiary)] rounded' />
      </div>
      <div className='space-y-2'>
        <div className='h-4 bg-[var(--color-bg-tertiary)] rounded w-full' />
        <div className='h-4 bg-[var(--color-bg-tertiary)] rounded w-3/4' />
        <div className='h-4 bg-[var(--color-bg-tertiary)] rounded w-1/2' />
      </div>
    </div>
  );
}

export function ResearchSection({ object }: ResearchSectionProps) {
  const orgId = object.org_id;
  const entityId = object.entity_id;
  const recordType =
    object.entity_type_name === 'ahoy_company' ? 'company' : 'contact';

  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { research, isLoading, error, refetch } = useResearch({
    orgId,
    entityId,
    recordType,
  });

  const { createResearch, isCreating: isCreatingResearch } = useCreateResearch({
    orgId,
    entityId,
    recordType,
  });

  const mostRecent = research[0] ?? null;
  const history = research.slice(1);

  // Reset selection when the latest research changes (e.g. new in-progress research appears)
  const prevMostRecentIdRef = useRef(mostRecent?.entity_id);
  useEffect(() => {
    if (
      mostRecent?.entity_id &&
      mostRecent.entity_id !== prevMostRecentIdRef.current
    ) {
      setSelectedId(null);
    }
    prevMostRecentIdRef.current = mostRecent?.entity_id;
  }, [mostRecent?.entity_id]);

  const displayedResearch = selectedId
    ? (research.find(r => r.entity_id === selectedId) ?? mostRecent)
    : mostRecent;

  const isViewingHistory = selectedId && selectedId !== mostRecent?.entity_id;

  // Email compose support (same pattern as v1 ResearchTab)
  const { user } = useAuthInfo();
  const createProposal = useCreateProposal(orgId);
  const { openProposal } = useProposalSidebar();
  const queryClient = useQueryClient();
  const contactEmailRaw = getPropertyValue<string | string[]>(
    object.properties?.ahoy_email
  );
  const contactEmail = Array.isArray(contactEmailRaw)
    ? contactEmailRaw[0]
    : contactEmailRaw;

  const handleSelectHistoryItem = useCallback((item: Research) => {
    setSelectedId(item.entity_id);
  }, []);

  const handleBackToLatest = useCallback(() => {
    setSelectedId(null);
  }, []);

  const toggleHistory = useCallback(() => {
    setHistoryOpen(prev => !prev);
  }, []);

  const updateViewDefinitionCache = useCallback(
    (researchEntityId: string, componentId: string, proposalId: number) => {
      const queryKey = ['research', orgId, entityId, recordType];
      queryClient.setQueryData<Research[]>(queryKey, oldData => {
        if (!oldData) {
          return oldData;
        }
        return oldData.map(item => {
          if (item.entity_id !== researchEntityId) {
            return item;
          }
          if (!item.view_definition_json) {
            return item;
          }
          try {
            const viewDef = JSON.parse(item.view_definition_json);
            type ComponentNode = {
              id: string;
              props?: Record<string, unknown>;
              children?: ComponentNode[];
            };
            const updateComponent = (components: ComponentNode[]): boolean => {
              for (const comp of components) {
                if (comp.id === componentId) {
                  comp.props = { ...comp.props, proposal_id: proposalId };
                  return true;
                }
                if (comp.children && updateComponent(comp.children)) {
                  return true;
                }
              }
              return false;
            };
            updateComponent(viewDef.components || []);
            return {
              ...item,
              view_definition_json: JSON.stringify(viewDef),
            };
          } catch {
            return item;
          }
        });
      });
    },
    [orgId, entityId, recordType, queryClient]
  );

  const handleComposeEmail = useCallback(
    async (data: {
      component_id: string;
      subject: string;
      body: string;
      context_label: string;
    }) => {
      if (!contactEmail || !user?.id) {
        return;
      }
      if (!data.subject?.trim() || !data.body?.trim()) {
        toast.error('Conversation starter is missing subject or body content');
        return;
      }
      const researchEntityId = displayedResearch?.entity_id;
      if (!researchEntityId) {
        return;
      }
      try {
        const request = quickEmailProposal({
          title: `Email: ${data.subject}`,
          description: `Conversation starter from research: ${data.context_label}`,
          assignedUserId: user.id,
          sourceTypeName: `ahoy_${recordType}_research`,
          sourceId: entityId,
          sourceContext: {
            channel: 'ahoy_research',
            account_identifier: user.email,
          },
          to: [contactEmail],
          subject: data.subject,
          body: data.body,
        });
        const proposal = await createProposal.mutateAsync(request);
        openProposal(proposal.id);
        try {
          await apiPatch(
            `/api/orgs/${orgId}/research/${researchEntityId}/components/${data.component_id}/`,
            { proposal_id: proposal.id }
          );
        } catch (patchError) {
          logger.error(
            'Failed to store proposal_id on conversation starter component',
            patchError instanceof Error ? patchError : undefined,
            {
              entity_id: researchEntityId,
              component_id: data.component_id,
              proposal_id: proposal.id,
            }
          );
        }
        updateViewDefinitionCache(
          researchEntityId,
          data.component_id,
          proposal.id
        );
      } catch (err) {
        logger.error(
          'Failed to create email proposal from conversation starter',
          err instanceof Error ? err : undefined,
          {
            entity_id: entityId,
            context_label: data.context_label,
            has_email: !!contactEmail,
          }
        );
        toast.error("We couldn't create the email draft. Try again.");
      }
    },
    [
      contactEmail,
      user,
      entityId,
      displayedResearch?.entity_id,
      orgId,
      recordType,
      createProposal,
      openProposal,
      updateViewDefinitionCache,
    ]
  );

  const handleOpenProposal = useCallback(
    (proposalId: number) => {
      openProposal(proposalId);
    },
    [openProposal]
  );

  const isResearchable = canResearch(object, recordType);

  const statusBadge = useMemo(
    () =>
      getResearchStatusBadge(
        displayedResearch ? getResearchOutcome(displayedResearch) : 'queued'
      ),
    [displayedResearch]
  );

  const researchAction = useMemo(
    () =>
      isResearchable ? (
        <SectionActionButton
          label={isCreatingResearch ? 'Researching…' : 'Research'}
          onClick={createResearch}
          icon={SearchMd}
          disabled={isCreatingResearch}
        />
      ) : null,
    [isResearchable, createResearch, isCreatingResearch]
  );

  // Collapsed-header précis. Without it the closed section is the same row
  // whether the record has three completed briefs or has never been
  // researched — and a queued, running or failed run is invisible until you
  // open it. Deliberately the same pill + timestamp the panel's own status
  // bar renders below, so the two can never tell different stories.
  const researchSummary = useMemo(() => {
    if (isLoading || error || !displayedResearch) {
      return null;
    }
    return (
      <>
        <BadgeWithDot color={statusBadge.color} size='sm'>
          {statusBadge.label}
        </BadgeWithDot>
        {displayedResearch.created_at && (
          <span className='truncate text-xs text-fg-tertiary'>
            {formatRelativeTime(displayedResearch.created_at)}
          </span>
        )}
      </>
    );
  }, [isLoading, error, displayedResearch, statusBadge]);

  let content: React.ReactNode;
  if (isLoading) {
    content = <LoadingSkeleton />;
  } else if (error) {
    content = (
      <QueryErrorState
        error={error}
        label="We couldn't load research"
        onRetry={refetch}
        size='sm'
      />
    );
  } else if (research.length === 0) {
    content = isResearchable ? (
      <p className='text-xs md:text-sm text-fg-secondary'>
        No research yet. Click Research to get started.
      </p>
    ) : (
      <ResearchNeedsMoreInfo recordType={recordType} />
    );
  } else {
    content = (
      <div className='space-y-4'>
        {/* Status bar */}
        {displayedResearch && (
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <BadgeWithDot color={statusBadge.color} size='sm'>
                {statusBadge.label}
              </BadgeWithDot>
              {isViewingHistory && (
                <button
                  type='button'
                  onClick={handleBackToLatest}
                  className='text-xs text-fg-brand-primary hover:underline cursor-pointer bg-transparent border-none p-0'
                >
                  ← Back to latest
                </button>
              )}
            </div>
            {displayedResearch.created_at && (
              <span className='text-xs text-fg-tertiary'>
                {formatRelativeTime(displayedResearch.created_at)}
              </span>
            )}
          </div>
        )}

        {/* Research detail */}
        {displayedResearch && (
          <ResearchDetail
            research={displayedResearch}
            object={object}
            onComposeEmail={contactEmail ? handleComposeEmail : undefined}
            onOpenProposal={handleOpenProposal}
            isComposing={createProposal.isPending}
          />
        )}

        {/* History accordion */}
        {history.length > 0 && (
          <div>
            <button
              type='button'
              onClick={toggleHistory}
              className='flex items-center gap-2 text-xs md:text-sm font-medium text-fg-secondary hover:text-fg-primary cursor-pointer bg-transparent border-none p-0'
            >
              <ChevronRight
                className={cx(
                  'size-4 transition-transform',
                  historyOpen && 'rotate-90'
                )}
              />
              Previous research ({history.length})
            </button>
            {historyOpen && (
              <div className='mt-2 space-y-2 pl-6'>
                {history.map(item => (
                  <ResearchListItem
                    key={item.entity_id}
                    research={item}
                    isSelected={item.entity_id === selectedId}
                    onClick={() => handleSelectHistoryItem(item)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    // Collapsed by default. A research brief is the longest thing the slideout
    // can render — expanded, it pushes notes, people, files and the timeline
    // below the fold on every contact and company that has ever been
    // researched. The header row keeps the section (and its Research action)
    // one click away; opening it is the deliberate act, and `summary` is what
    // tells the user there is something in there worth the click.
    <CollapsibleSection
      title='Research'
      actions={researchAction}
      summary={researchSummary}
      defaultExpanded={false}
    >
      {content}
    </CollapsibleSection>
  );
}
